// Stripe payment utilities
import { loadSettings } from './firebaseSettings';

/**
 * Create a checkout session for subscription
 */
export const createCheckoutSession = async (priceId, userEmail) => {
  try {
    console.log('Creating checkout session...');
    console.log('Price ID:', priceId);
    console.log('User email:', userEmail);
    console.log('Secret key exists:', !!process.env.REACT_APP_STRIPE_SECRET_KEY);

    if (!priceId) {
      throw new Error('Price ID is undefined. Check your .env file.');
    }

    if (!process.env.REACT_APP_STRIPE_SECRET_KEY) {
      throw new Error('Stripe secret key is not configured. Check your .env file.');
    }

    // Check if user has used trial before from Firebase settings
    const settings = await loadSettings(userEmail);
    const hasUsedTrial = settings.hasUsedTrial || false;
    console.log('User has used trial before (from Firebase):', hasUsedTrial);

    const params = {
      'success_url': `${window.location.origin}?session_id={CHECKOUT_SESSION_ID}`,
      'cancel_url': window.location.origin,
      'mode': 'subscription',
      'customer_email': userEmail,
      'line_items[0][price]': priceId,
      'line_items[0][quantity]': '1',
    };

    // Only add trial if customer hasn't used it before
    if (!hasUsedTrial) {
      params['subscription_data[trial_period_days]'] = '3';
      console.log('Adding 3-day trial for new customer');
    } else {
      console.log('No trial - customer has previous subscription history');
    }

    console.log('Price ID:', priceId);
    console.log('Params:', params);

    console.log('Sending request to Stripe...');

    const response = await fetch('https://api.stripe.com/v1/checkout/sessions', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${process.env.REACT_APP_STRIPE_SECRET_KEY}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams(params),
    });

    console.log('Response status:', response.status);

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Stripe API error:', errorData);
      throw new Error(errorData.error?.message || 'Failed to create checkout session');
    }

    const session = await response.json();
    console.log('Session created successfully:', session.id);

    // Redirect directly to Stripe Checkout URL
    window.location.href = session.url;

    return session;
  } catch (error) {
    console.error('Stripe checkout error:', error);
    throw error;
  }
};

/**
 * Check subscription status and plan by fetching from Stripe
 * Returns: { hasSubscription: boolean, plan: 'basic' | 'pro' | null }
 */
export const checkSubscriptionStatus = async (userEmail) => {
  try {
    const devBypassEmails = new Set(['support@qaly.co']);
    if (userEmail && devBypassEmails.has(userEmail.toLowerCase())) {
      console.log('🧪 Dev bypass enabled for subscription check:', userEmail);
      return {
        hasSubscription: true,
        plan: 'pro',
        trialEnd: null,
        isOnTrial: false,
        cancelAtPeriodEnd: false,
        currentPeriodEnd: null,
        currentPeriodStart: null,
        billingCycleAnchor: null
      };
    }

    // Search for customer by email
    const response = await fetch(`https://api.stripe.com/v1/customers?email=${encodeURIComponent(userEmail)}&limit=1`, {
      headers: {
        'Authorization': `Bearer ${process.env.REACT_APP_STRIPE_SECRET_KEY}`,
      },
    });

    if (!response.ok) {
      console.error('Failed to fetch customer from Stripe');
      return { hasSubscription: false, plan: null };
    }

    const data = await response.json();

    if (data.data.length === 0) {
      // No customer found
      return { hasSubscription: false, plan: null };
    }

    const customer = data.data[0];
    console.log('✅ Found Stripe customer:', customer.id);

    // Save customer ID for later use
    saveCustomerId(userEmail, customer.id);

    // Check if customer has active or trialing subscriptions
    const subsResponse = await fetch(`https://api.stripe.com/v1/subscriptions?customer=${customer.id}&limit=1`, {
      headers: {
        'Authorization': `Bearer ${process.env.REACT_APP_STRIPE_SECRET_KEY}`,
      },
    });

    if (!subsResponse.ok) {
      console.error('Failed to fetch subscriptions from Stripe');
      return { hasSubscription: false, plan: null };
    }

    const subsData = await subsResponse.json();

    // Filter for active or trialing subscriptions
    const activeSubscriptions = subsData.data.filter(sub =>
      sub.status === 'active' || sub.status === 'trialing'
    );

    if (activeSubscriptions.length === 0) {
      console.log('❌ No active subscription');
      return { hasSubscription: false, plan: null, trialEnd: null, isOnTrial: false };
    }

    const subscription = activeSubscriptions[0];

    // Log the full subscription object to debug
    console.log('🔍 Full subscription object:', subscription);

    const priceId = subscription.items.data[0]?.price?.id;
    const trialEnd = subscription.trial_end;
    const isOnTrial = trialEnd && trialEnd * 1000 > Date.now();
    const cancelAtPeriodEnd = subscription.cancel_at_period_end;
    const currentPeriodEnd = subscription.current_period_end;
    const currentPeriodStart = subscription.current_period_start;
    const billingCycleAnchor = subscription.billing_cycle_anchor;

    console.log('✅ Active subscription found');
    console.log('Subscription status:', subscription.status);
    console.log('Raw current_period_end from Stripe:', subscription.current_period_end);
    console.log('Raw current_period_start from Stripe:', subscription.current_period_start);
    console.log('Price ID:', priceId);
    console.log('Trial end:', trialEnd ? new Date(trialEnd * 1000) : 'No trial');
    console.log('Is on trial:', isOnTrial);
    console.log('Cancel at period end:', cancelAtPeriodEnd);
    console.log('Current period start:', currentPeriodStart ? new Date(currentPeriodStart * 1000) : 'Unknown');
    console.log('Current period end:', currentPeriodEnd ? new Date(currentPeriodEnd * 1000) : 'Unknown');
    console.log('Billing cycle anchor:', billingCycleAnchor ? new Date(billingCycleAnchor * 1000) : 'Unknown');

    // Determine which plan
    let plan = null;
    if (priceId === process.env.REACT_APP_STRIPE_BASIC_PRICE_ID) {
      plan = 'basic';
    } else if (priceId === process.env.REACT_APP_STRIPE_PRO_PRICE_ID) {
      plan = 'pro';
    }

    console.log('📦 Subscription plan:', plan);

    // Fallback logic for missing period dates
    // Use trial_end if currentPeriodEnd is missing, or use subscription created date + 30 days
    let effectivePeriodEnd = currentPeriodEnd ? currentPeriodEnd * 1000 : null;

    if (!effectivePeriodEnd && trialEnd) {
      // Use trial end if available
      effectivePeriodEnd = trialEnd * 1000;
      console.log('⚠️ currentPeriodEnd missing, using trialEnd as fallback');
    } else if (!effectivePeriodEnd && subscription.created) {
      // Calculate next billing date as 30 days from creation
      effectivePeriodEnd = (subscription.created + (30 * 24 * 60 * 60)) * 1000;
      console.log('⚠️ currentPeriodEnd missing, calculating from created date');
    }

    const effectivePeriodStart = currentPeriodStart
      ? currentPeriodStart * 1000
      : (subscription.created ? subscription.created * 1000 : null);

    console.log('🔄 Using effective period end:', effectivePeriodEnd ? new Date(effectivePeriodEnd) : 'null');
    console.log('🔄 Using effective period start:', effectivePeriodStart ? new Date(effectivePeriodStart) : 'null');

    return {
      hasSubscription: true,
      plan,
      trialEnd: trialEnd ? trialEnd * 1000 : null,
      isOnTrial,
      cancelAtPeriodEnd,
      currentPeriodEnd: effectivePeriodEnd,
      currentPeriodStart: effectivePeriodStart,
      billingCycleAnchor: billingCycleAnchor ? billingCycleAnchor * 1000 : null
    };
  } catch (error) {
    console.error('Error checking subscription status:', error);
    return { hasSubscription: false, plan: null };
  }
};


/**
 * Store Stripe customer ID locally
 * In production, this should be stored in your database
 */
export const saveCustomerId = (userEmail, customerId) => {
  localStorage.setItem(`stripe_customer_${userEmail}`, customerId);
};

/**
 * Get stored Stripe customer ID
 */
export const getCustomerId = (userEmail) => {
  return localStorage.getItem(`stripe_customer_${userEmail}`);
};

/**
 * Create a customer portal session for managing subscription
 */
export const createPortalSession = async (customerId) => {
  try {
    const response = await fetch('https://api.stripe.com/v1/billing_portal/sessions', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${process.env.REACT_APP_STRIPE_SECRET_KEY}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        'customer': customerId,
        'return_url': window.location.origin,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to create portal session');
    }

    const session = await response.json();

    // Redirect to customer portal
    window.location.href = session.url;

    return session;
  } catch (error) {
    console.error('Portal session error:', error);
    throw error;
  }
};
